import { Injectable } from '@angular/core';
import {
	HttpRequest,
	HttpHandler,
	HttpEvent,
	HttpInterceptor,
	HttpErrorResponse,
	HttpResponse
} from '@angular/common/http';
import {BehaviorSubject, Observable, of, throwError} from 'rxjs';
import {AuthService} from 'app/auth/auth.service';
import {catchError, filter, switchMap, take, tap, takeUntil} from 'rxjs/operators';
import {ACCESS_TOKEN_REFRESHED, AUTH_PATH, AUTHENTICATE_PATH, LOGGED_OUT_PATH, REFRESH_TOKEN, REVOKE_TOKEN_PATH} from 'shared/magic';
import {NavigationStart, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import * as userActions from 'app/_actions/user.actions';
import {RestService} from 'shared/_services/rest.service';
import {AccessTokenRefresh} from '../../../journal/src/_interface/access-token-refresh.interface';
import {EditModeService} from 'shared/_services/edit-mode.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	private isRefreshing = false;
	private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
	private isDisableEdit = false;
	private saveEntryRequest: HttpRequest<any> | null = null;
	private logoutAfterSavingEntry = false;

	constructor(
		private authService: AuthService,
		private router: Router,
		private store: Store,
		private restService: RestService,
		private editModeService: EditModeService
		) {}

	private handleTokenRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return this.authService.refreshToken().pipe(
			catchError(this.handleRefreshError.bind(this)),
			switchMap((res: AccessTokenRefresh) => this.handleRefreshResponse(res, request, next))
		);
	}

	private handleRefreshResponse(res: AccessTokenRefresh, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		this.updateRefreshState(res);

		if (this.logoutAfterSavingEntry) {
			return this.handleSaveAndLogout(request, next);
		}

		return next.handle(request.clone());
	}

	private handleSaveAndLogout(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const isEntrySave = request.method === 'PUT' && request.url.includes('entries');
		this.isDisableEdit = request.url.includes('disable-edit');

		if (isEntrySave || this.isDisableEdit || this.saveEntryRequest) {
			return this.handleEditModeAndSave(request, next);
		}

		return this.handleTokenRevoked(ACCESS_TOKEN_REFRESHED);
	}

	private handleEditModeAndSave(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return this.editModeService.forceDisable().pipe(
			switchMap(() => {
				if (this.isDisableEdit || this.saveEntryRequest) {
					return this.handleSaveRequest(next);
				}
				return this.handleResponseAndLogout(request.clone(), next);
			})
		);
	}

	private handleSaveRequest(next: HttpHandler): Observable<HttpEvent<any>> {
		const saveRequest = new HttpRequest(
			'PUT',
			this.saveEntryRequest.url,
			this.saveEntryRequest.body,
			{
				headers: this.saveEntryRequest.headers,
				withCredentials: true
			}
		);
		return this.handleResponseAndLogout(saveRequest, next);
	}

	private handleResponseAndLogout(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(request).pipe(
			switchMap(event => {
				if (event instanceof HttpResponse) {
					return this.handleTokenRevoked(ACCESS_TOKEN_REFRESHED);
				}
				return of(event);
			})
		);
	}

	private updateRefreshState(res: AccessTokenRefresh): void {
		this.isRefreshing = false;
		this.store.dispatch(userActions.tokenRefreshing({ isRefreshing: this.isRefreshing }));
		this.refreshTokenSubject.next(res);
		this.logoutAfterSavingEntry = res?.message === ACCESS_TOKEN_REFRESHED;
	}

	private handleRefreshError(err: any): Observable<never> {
		this.isRefreshing = false;
		this.store.dispatch(userActions.tokenRefreshing({ isRefreshing: this.isRefreshing }));
		this.refreshTokenSubject.complete();
		this.refreshTokenSubject = new BehaviorSubject<any>(null);
		this.restService.setAuthenticationStatus(false);
		this.authService.logout();
		this.router.navigate([`/${LOGGED_OUT_PATH}`]);
		return throwError(err);
	}

	private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.store.dispatch(userActions.tokenRefreshing({ isRefreshing: this.isRefreshing }));
			this.refreshTokenSubject.next(null);
			return this.handleTokenRefresh(request, next);
		}
		return this.refreshTokenSubject.pipe(
			filter(response => response !== null),
			take(1),
			switchMap(() => this.handlePendingRequest(request, next))
		);
	}

	private handlePendingRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (this.logoutAfterSavingEntry && request.method === 'PUT' && request.url.includes('entries')) {
			this.isDisableEdit = request.url.includes('disable-edit');
			this.saveEntryRequest = request;
			return of(null);
		}
		return next.handle(request);
	}

	private getLogoutNavigation(): Observable<NavigationStart> {
		return this.router.events.pipe(
			filter((event): event is NavigationStart =>
				event instanceof NavigationStart &&
				event.url.includes(LOGGED_OUT_PATH)
			)
		);
	}

	private handleAccessTokenRefreshed(response: HttpEvent<any>): void {
		if (response instanceof HttpResponse && response.body?.message === ACCESS_TOKEN_REFRESHED) {
			this.logoutAfterSavingEntry = true;
		}
	}

	private handleInterceptorError(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler): Observable<any> {
		if (error.status === 401) {
			return this.handle401Error(request, next);
		}
		return throwError(error);
	}

	private handleTokenRevoked(message?: string): Observable<HttpEvent<any>> {
		this.authService.logout();
		this.restService.setAuthenticationStatus(false);
		return new Observable(observer => {
			this.router.navigate([`/${LOGGED_OUT_PATH}`], {
				state: { message },
				replaceUrl: true
			});
			observer.complete();
		});
	}

	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<any>> {
		const urlSegments = request.url.split('/');
		const isExcluded = urlSegments.includes(AUTH_PATH)
			|| urlSegments.includes(AUTHENTICATE_PATH)
			|| urlSegments.includes(REFRESH_TOKEN)
			|| urlSegments.includes(REVOKE_TOKEN_PATH);
		if (isExcluded) {
			return next.handle(request);
		}
		return next.handle(request).pipe(
			takeUntil(this.getLogoutNavigation()),
			tap(this.handleAccessTokenRefreshed.bind(this)),
			catchError(error => this.handleInterceptorError(error, request, next))
		);
	}
}
